import { useState } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, Tag, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { validatePromo } from '../lib/api';
import { useLang } from '../lib/lang';

/**
 * Reusable promo code input — validates server-side then exposes the applied
 * promo to the parent via the `onApplied(promo|null)` callback.
 *
 * Props:
 *  - context: { pack_id?, raffle_id?, plan_id?, amount? } – passed to validate
 *  - onApplied(promo): called with the validated promo object (or null when removed)
 *  - applied: external applied state (parent owns it). { code, final_discount, message, ... }
 *  - variant: 'light' (cream bg) | 'dark' (rolex-green bg) — for visual contrast
 *  - className: optional extra classes
 */
export default function PromoCodeInput({ context = {}, applied = null, onApplied, variant = 'light', className = '' }) {
  const { t } = useLang();
  const [code, setCode] = useState('');
  const [validating, setValidating] = useState(false);

  const handleApply = async () => {
    const trimmed = (code || '').trim().toUpperCase();
    if (!trimmed) {
      toast.error(t('promoEmptyError') || 'Please enter a promo code');
      return;
    }
    setValidating(true);
    try {
      const payload = { code: trimmed };
      if (context.pack_id) payload.pack_id = context.pack_id;
      if (context.raffle_id) payload.raffle_id = context.raffle_id;
      if (context.plan_id) payload.plan_id = context.plan_id;
      if (context.amount != null) payload.amount = context.amount;
      const res = await validatePromo(payload);
      const promo = res.data;
      onApplied?.(promo);
      toast.success(promo.message || (t('promoApplied') || 'Promo code applied!'));
      setCode('');
    } catch (e) {
      const msg = e?.response?.data?.detail || (t('promoInvalid') || 'Invalid promo code');
      toast.error(msg);
      onApplied?.(null);
    } finally {
      setValidating(false);
    }
  };

  const handleRemove = () => {
    onApplied?.(null);
    setCode('');
  };

  const isDark = variant === 'dark';
  const baseInputClass = isDark
    ? 'bg-[hsl(var(--rolex-green-elev)/0.5)] border-[hsl(var(--gold)/0.30)] text-cream placeholder:text-cream/45'
    : 'bg-white border-gold/30 text-rolex-green placeholder:text-rolex-green/45';
  const labelClass = isDark ? 'text-gold' : 'text-gold-dark';
  const wrapperClass = isDark
    ? 'bg-[hsl(var(--rolex-green-elev)/0.40)] border-[hsl(var(--gold)/0.25)]'
    : 'bg-cream border-gold/25';

  if (applied && applied.code) {
    return (
      <div className={`rounded-xl border-2 border-emerald-500/60 bg-emerald-50 p-4 ${className}`} data-testid="promo-applied">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
            <Check className="w-5 h-5 text-white" strokeWidth={3} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <Badge className="bg-emerald-600 text-white font-mono text-xs px-2 py-0.5 border-none">
                {applied.code}
              </Badge>
              <span className="text-xs text-emerald-700 font-semibold">{t('promoCodeApplied') || 'Code applied!'}</span>
            </div>
            <p className="text-xs text-emerald-800/80">
              {applied.message || (
                <>
                  {applied.discount_pct > 0 && `-${applied.discount_pct}% `}
                  {applied.discount_amount > 0 && `-£${applied.discount_amount.toFixed(0)} `}
                  {applied.bonus_points > 0 && `+£${applied.bonus_points} bonus `}
                </>
              )}
            </p>
            {applied.final_discount > 0 && (
              <p className="text-sm font-bold text-emerald-900 mt-1">
                {t('promoDiscount') || 'Discount'} : -£{Number(applied.final_discount).toFixed(2)}
              </p>
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            className="text-emerald-700 hover:bg-emerald-100 hover:text-emerald-900"
            data-testid="promo-remove-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border ${wrapperClass} p-4 ${className}`} data-testid="promo-input-wrapper">
      <label className={`flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold ${labelClass} mb-2`}>
        <Tag className="w-3.5 h-3.5" />
        <span>{t('promoCodeLabel') || 'Promo code'}</span>
        <span className={`${isDark ? 'text-cream/45' : 'text-rolex-green/45'} font-normal normal-case tracking-normal`}>
          ({t('optional') || 'optional'})
        </span>
      </label>
      <div className="flex gap-2">
        <Input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApply(); } }}
          placeholder={t('promoCodePlaceholder') || 'WELCOME10'}
          className={`flex-1 font-mono tracking-widest uppercase ${baseInputClass}`}
          maxLength={32}
          disabled={validating}
          data-testid="promo-code-field"
        />
        <Button
          type="button"
          onClick={handleApply}
          disabled={validating || !code.trim()}
          className="btn-gold gap-2 shrink-0 px-5"
          data-testid="promo-apply-btn"
        >
          {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />}
          {t('apply') || 'Apply'}
        </Button>
      </div>
    </div>
  );
}
